// Named picks for tour actions (Help pane live tours). A tour author does not
// know the job the tour will run on, so a step's argument may name a pick,
// `{ $pick: 'first_folder' }`, which is resolved at run time against the
// mounted page's own read tools (find_toc_item on Tables; find_variable and
// read_spec on Edit Spec). A pick's result is kept for the run, so a later
// step can build on it (`first_table_in_picked_folder`,
// `last_added_axis_item`).
//
// Pure: the tools are passed in, so the tests run against a fake page.

import type { VoiceUITool } from '../voice/types'

export type PickName = 'first_folder' | 'first_table_in_picked_folder' | 'first_unused_variable' | 'last_added_axis_item'

interface TocItem {
  path: string
  name?: string
  kind?: string
}

const MY_TABLES_PREFIX = 'tables/user/'

/** Run one of the page's tools and parse its JSON result; undefined when it is not on the page. */
interface PageResult {
  items?: TocItem[]
  variables?: Array<{ name: string; on_axis?: string; code_count?: number }>
  side?: Array<{ id: string; variable?: string }>
  top?: Array<{ id: string; variable?: string }>
  error?: string
}

async function call(tools: VoiceUITool[], name: string, args: Record<string, unknown>): Promise<PageResult | undefined> {
  const tool = tools.find((t) => t.name === name)
  if (!tool) return undefined
  try {
    return JSON.parse(String(await tool.execute(args)))
  } catch {
    return { error: `${name} failed` }
  }
}

const NOT_ON_PAGE = 'not on this page'

async function tablesUnder(tools: VoiceUITool[], folder: string): Promise<TocItem[] | undefined> {
  const r = await call(tools, 'find_toc_item', { query: folder, kind: 'table' })
  if (!r) return undefined
  const prefix = `${folder.toLowerCase()}/`
  return (r.items ?? []).filter((t) => t.path.toLowerCase().startsWith(prefix))
}

/**
 * - first_folder: the first shared folder (not under My Tables) that holds a
 *   table; else the first folder of any kind that does. Its path.
 * - first_table_in_picked_folder: the first table under the folder
 *   first_folder picked. Its path.
 * - first_unused_variable (Edit Spec): the first variable in the tree that
 *   has codes and is on neither axis. Its name.
 * - last_added_axis_item (Edit Spec): the item the variable
 *   first_unused_variable picked became (the last one, if added twice); with
 *   no such pick, the last item on the rows, else on the columns. Its id.
 */
export const PICKS: Record<PickName, (tools: VoiceUITool[], picked: Record<string, string>) => Promise<{ value: string } | { error: string }>> = {
  first_folder: async (tools) => {
    const top = await call(tools, 'find_toc_item', { query: '' })
    if (!top) return { error: NOT_ON_PAGE }
    const folders = (top.items ?? []).filter((i) => i.kind === 'folder' && i.path)
    const shared = folders.filter((f) => !f.path.toLowerCase().startsWith(MY_TABLES_PREFIX))
    const mine = folders.filter((f) => f.path.toLowerCase().startsWith(MY_TABLES_PREFIX))
    for (const f of [...shared, ...mine]) {
      if ((await tablesUnder(tools, f.path))?.length) return { value: f.path }
    }
    return { error: 'no folder with tables is on this job' }
  },
  first_table_in_picked_folder: async (tools, picked) => {
    const folder = picked.first_folder
    if (!folder) return { error: 'no folder was picked before this step' }
    const tables = await tablesUnder(tools, folder)
    if (!tables) return { error: NOT_ON_PAGE }
    return tables.length ? { value: tables[0].path } : { error: `no table is in ${folder}` }
  },
  first_unused_variable: async (tools) => {
    const r = await call(tools, 'find_variable', { query: '', codes: true })
    if (!r) return { error: NOT_ON_PAGE }
    const vars = r.variables ?? []
    if (vars.length === 0) return { error: 'the variable tree has not loaded yet' }
    const free = vars.find((v) => !v.on_axis && (v.code_count ?? 0) > 0)
    return free ? { value: free.name } : { error: 'no variable with codes is free to add on this job' }
  },
  last_added_axis_item: async (tools, picked) => {
    const r = await call(tools, 'read_spec', {})
    if (!r) return { error: NOT_ON_PAGE }
    const items = [...(r.side ?? []), ...(r.top ?? [])]
    const variable = picked.first_unused_variable?.toLowerCase()
    const added = variable ? items.filter((i) => i.variable?.toLowerCase() === variable).pop() : undefined
    const item = added ?? r.side?.[r.side.length - 1] ?? r.top?.[r.top.length - 1]
    return item ? { value: item.id } : { error: 'there is no item on the rows or columns yet' }
  },
}

export function isPickName(name: unknown): name is PickName {
  return typeof name === 'string' && Object.prototype.hasOwnProperty.call(PICKS, name)
}

/** The pick a value names, when it is `{ $pick: name }`. */
function pickOf(value: unknown): string | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const v = value as Record<string, unknown>
  return Object.keys(v).length === 1 && typeof v.$pick === 'string' ? v.$pick : undefined
}

/** Resolve one pick, reusing a result the run already has. */
export async function resolvePick(
  name: string,
  tools: VoiceUITool[],
  picked: Record<string, string>,
): Promise<{ value: string } | { error: string }> {
  if (picked[name]) return { value: picked[name] }
  if (!isPickName(name)) return { error: `unknown pick "${name}"` }
  return PICKS[name](tools, picked)
}

/**
 * Replace every `{ $pick: name }` value in a step's args with its result.
 * Returns the args and the run's picks with any new ones, or the first
 * pick's error.
 */
export async function resolveArgs(
  args: Record<string, unknown> | undefined,
  tools: VoiceUITool[],
  picked: Record<string, string>,
): Promise<{ args: Record<string, unknown>; picked: Record<string, string> } | { error: string }> {
  const out: Record<string, unknown> = {}
  let have = picked
  for (const [key, value] of Object.entries(args ?? {})) {
    const name = pickOf(value)
    if (!name) {
      out[key] = value
      continue
    }
    const r = await resolvePick(name, tools, have)
    if ('error' in r) return { error: r.error }
    out[key] = r.value
    if (have[name] !== r.value) have = { ...have, [name]: r.value }
  }
  return { args: out, picked: have }
}

/** The error a pick gives when the page has not mounted its tools yet. Worth a retry. */
export const PICK_NOT_ON_PAGE = NOT_ON_PAGE
